// PermitForm.tsx
// Adaptive permit creation form for Re-GenOps
// - Uses React Query for API integration (mutation)
// - Uses fetch for HTTP requests
// - Accepts permitType and highRisk from wizard or manual selection
// - On submit, POSTs to /api/v1/permits/ and shows loading/success/error
//
// API integration approach:
// - React Query handles mutation, loading, error, and success states
// - fetch is used for the actual HTTP request
// - On success, you can redirect or show a confirmation

import React, { useState, useCallback, useEffect } from 'react';
import { Box, Button, TextField, MenuItem, Typography, Checkbox, FormControlLabel, FormGroup, Select, InputLabel, FormControl, Alert, CircularProgress, Stack, Grid, Divider } from '@mui/material';
import { AccessTime, CalendarToday, Business, LocationOn, Build } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useSiteEquipment } from '../../hooks/useSiteEquipment';
import UserSearchAutocomplete from '../../components/UserSearchAutocomplete';

const permitTypes = [
  { value: 'access', label: 'Access Permit' },
  { value: 'work_authority', label: 'Work Authority' },
  { value: 'general_work', label: 'General Work' },
];

const highRiskOptions = [
  { label: 'Confined Space Entry', value: 'confined_space' },
  { label: 'Working at Heights', value: 'heights' },
  { label: 'Excavation', value: 'excavation' },
  { label: 'Hot Works', value: 'hot_works' },
];

interface PermitFormProps {
  permitType?: string;
  highRisk?: string[];
}

const PermitForm: React.FC<PermitFormProps> = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { permitType?: string; highRisk?: string[] } | undefined;
  const [permitType, setPermitType] = useState(state?.permitType || props.permitType || '');
  const [highRisk, setHighRisk] = useState<string[]>(state?.highRisk || props.highRisk || []);
  const [form, setForm] = useState({
    title: '',
    location: '',
    description: '',
    start: '',
    end: '',
    recipient_id: '',
    supervisor_id: '',
    // ...other fields as needed
  });

  // Site/Equipment selection hook
  const siteEquipment = useSiteEquipment();

  // Set default end time to end of day when component loads
  useEffect(() => {
    if (!form.end) {
      const endOfDay = new Date();
      endOfDay.setHours(23, 59, 59, 999);
      const endTimeString = endOfDay.toISOString().slice(0, 16);
      setForm(f => ({ ...f, end: endTimeString }));
    }
  }, [form.end]);

  // Prepopulate form when equipment/task selection changes
  useEffect(() => {
    if (siteEquipment.prepopulateData) {
      const data = siteEquipment.prepopulateData;
      
      // Update form with suggested data
      setForm(f => ({
        ...f,
        description: data.suggested_work_description || f.description,
        location: data.equipment.full_location || `${data.equipment.site_name} - ${data.equipment.name}` || f.location,
      }));

      // Update high-risk activities
      if (data.suggested_high_risk_activities.length > 0) {
        setHighRisk(data.suggested_high_risk_activities);
      }

      // Update permit type if not already set by wizard
      if (!props.permitType && !state?.permitType) {
        setPermitType(data.suggested_permit_type);
      }
    }
  }, [siteEquipment.prepopulateData, props.permitType, state?.permitType]);

  // Stable form change handlers to prevent input focus loss
  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(f => ({ ...f, description: e.target.value }));
  }, []);

  const handleLocationChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(f => ({ ...f, location: e.target.value }));
  }, []);

  const handleStartTimeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(f => ({ ...f, start: e.target.value }));
  }, []);

  const handleEndTimeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(f => ({ ...f, end: e.target.value }));
  }, []);



  // Date/time helper functions
  const setStartTimeToNow = useCallback(() => {
    const now = new Date();
    const nowString = now.toISOString().slice(0, 16);
    setForm(f => ({ ...f, start: nowString }));
  }, []);

  const addDayToEndTime = useCallback(() => {
    const startTime = form.start ? new Date(form.start) : new Date();
    const endTime = new Date(startTime);
    endTime.setDate(endTime.getDate() + 1);
    endTime.setHours(23, 59, 59, 999); // End of day
    const endTimeString = endTime.toISOString().slice(0, 16);
    setForm(f => ({ ...f, end: endTimeString }));
  }, [form.start]);

  const addWeekToEndTime = useCallback(() => {
    const startTime = form.start ? new Date(form.start) : new Date();
    const endTime = new Date(startTime);
    endTime.setDate(endTime.getDate() + 7);
    endTime.setHours(23, 59, 59, 999); // End of day
    const endTimeString = endTime.toISOString().slice(0, 16);
    setForm(f => ({ ...f, end: endTimeString }));
  }, [form.start]);

  // React Query mutation for permit creation
  const createPermit = async (data: any) => {
    const res = await fetch(`/api/v1/permits/permits/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include session cookies
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const error = await res.text();
      throw new Error(`Failed to create permit: ${error}`);
    }
    return res.json();
  };

  const mutation = useMutation({
    mutationFn: createPermit,
    onSuccess: (data) => {
      // Redirect to permit detail or show confirmation
      navigate(`/permits/${data.id}`);
    },
  });

  // Handle high-risk checkbox changes
  const handleHighRiskChange = (value: string, checked: boolean) => {
    setHighRisk(hr => checked ? [...hr, value] : hr.filter(v => v !== value));
  };

  // Dynamic fields based on permit type
  const renderTypeSpecificFields = () => {
    switch (permitType) {
      case 'access':
        return <TextField label="IASMs / Boundary Definition" fullWidth margin="normal" />;
      case 'test':
        return <TextField label="Energization Checklist" fullWidth margin="normal" />;
      case 'work_authority':
        return <TextField label="RASM Register" fullWidth margin="normal" />;
      case 'general_work':
        return <TextField label="Asset Owner Sign-off" fullWidth margin="normal" />;
      default:
        return null;
    }
  };

  // Handle form submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!permitType || !form.description || !form.location || !form.start || !form.end || !form.recipient_id) {
      alert('Please fill in all required fields, including selecting a recipient');
      return;
    }
    
    if (!siteEquipment.selectedSite) {
      alert('Please select a site for this permit');
      return;
    }
    
    // Map frontend fields to backend API fields
    const payload = {
      work_description: form.description,
      work_location: form.location,
      requested_start_time: form.start,
      requested_end_time: form.end,
      permit_type: permitType,
      high_risk: highRisk,
      recipient_id: parseInt(form.recipient_id),
      supervisor_id: form.supervisor_id && form.supervisor_id !== '' ? parseInt(form.supervisor_id) : undefined,
      site_id: siteEquipment.selectedSite, // Include selected site
      // Add more fields as needed
    };
    mutation.mutate(payload);
  };

  return (
    <Box maxWidth={600} mx="auto" mt={4}>
      <Typography variant="h4" color="primary" gutterBottom>
        Create General Work Permit
      </Typography>
      
      {/* Notice about Access and Test Permits */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Need an Access Permit or Test Permit?
        </Typography>
        <Typography variant="body2" gutterBottom>
          Access Permits and Test Permits require comprehensive safety measures, equipment tracking, 
          and detailed workflow management as per Re-GenOps requirements.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/permits/comprehensive')}
          sx={{ mt: 1 }}
        >
          Create Access/Test Permit
        </Button>
      </Alert>

      {/* Site/Equipment Selection */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
          Site & Equipment Selection
        </Typography>
        <Typography variant="body2">
          Select your site, location, and equipment to automatically populate permit details.
        </Typography>
      </Alert>
      
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Site</InputLabel>
            <Select
              value={siteEquipment.selectedSite || ''}
              label="Site"
              onChange={(e) => siteEquipment.handleSiteChange(e.target.value ? Number(e.target.value) : null)}
            >
              <MenuItem value="">
                <em>Select Site</em>
              </MenuItem>
              {siteEquipment.sites.map(site => (
                <MenuItem key={site.id} value={site.id}>
                  {site.code} - {site.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <FormControl fullWidth disabled={!siteEquipment.selectedSite}>
            <InputLabel>Location (Optional)</InputLabel>
            <Select
              value={siteEquipment.selectedLocation || ''}
              label="Location (Optional)"
              onChange={(e) => siteEquipment.handleLocationChange(e.target.value ? Number(e.target.value) : null)}
            >
              <MenuItem value="">
                <em>Select Location</em>
              </MenuItem>
              {siteEquipment.locations.map(location => (
                <MenuItem key={location.id} value={location.id}>
                  {location.name} {location.building && `(${location.building})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <FormControl fullWidth disabled={!siteEquipment.selectedSite || siteEquipment.equipment.length === 0}>
            <InputLabel>Equipment</InputLabel>
            <Select
              value={siteEquipment.selectedEquipment || ''}
              label="Equipment"
              onChange={(e) => siteEquipment.handleEquipmentChange(e.target.value ? Number(e.target.value) : null)}
            >
              <MenuItem value="">
                <em>Select Equipment</em>
              </MenuItem>
              {siteEquipment.equipment.map(eq => (
                <MenuItem key={eq.id} value={eq.id}>
                  {eq.equipment_number} - {eq.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <FormControl fullWidth disabled={!siteEquipment.selectedEquipment || siteEquipment.standardTasks.length === 0}>
            <InputLabel>Standard Task (Optional)</InputLabel>
            <Select
              value={siteEquipment.selectedTask || ''}
              label="Standard Task (Optional)"
              onChange={(e) => siteEquipment.handleTaskChange(e.target.value ? Number(e.target.value) : null)}
            >
              <MenuItem value="">
                <em>Custom Work</em>
              </MenuItem>
              {siteEquipment.standardTasks.map(task => (
                <MenuItem key={task.id} value={task.id}>
                  {task.task_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {siteEquipment.loading && (
        <Box display="flex" alignItems="center" justifyContent="center" py={2} sx={{ mb: 2 }}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2">Loading equipment data...</Typography>
        </Box>
      )}
      
      {siteEquipment.error && (
        <Alert severity="error" sx={{ mb: 2 }}>{siteEquipment.error}</Alert>
      )}
      
      {siteEquipment.prepopulateData && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            ✓ Equipment data loaded! Form has been pre-populated with suggested values.
          </Typography>
        </Alert>
      )}

      <Divider sx={{ mb: 3 }} />
      <form onSubmit={handleSubmit}>
        {/* Permit Type Selection */}
        <FormControl fullWidth margin="normal" required>
          <InputLabel>Permit Type</InputLabel>
          <Select
            value={permitType}
            label="Permit Type"
            onChange={e => setPermitType(e.target.value)}
            disabled={!!(props.permitType || state?.permitType)}
          >
            {permitTypes.map(pt => (
              <MenuItem key={pt.value} value={pt.value}>{pt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* User Selections */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <UserSearchAutocomplete
              siteId={siteEquipment.selectedSite}
              roleFilter="recipients"
              value={form.recipient_id ? parseInt(form.recipient_id) : null}
              onChange={(userId) => setForm(f => ({ ...f, recipient_id: userId ? userId.toString() : '' }))}
              label="Recipient *"
              placeholder="Search for permit recipient..."
              required
              permitType={permitType}
              helperText="Select a user who can receive this permit type"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <UserSearchAutocomplete
              siteId={siteEquipment.selectedSite}
              roleFilter="supervisors"
              value={form.supervisor_id ? parseInt(form.supervisor_id) : null}
              onChange={(userId) => setForm(f => ({ ...f, supervisor_id: userId ? userId.toString() : '' }))}
              label="Supervisor"
              placeholder="Search for supervisor..."
              helperText="Optional: Select a supervisor for this work"
            />
          </Grid>
        </Grid>

        {/* Issuer Info */}
        <TextField
          label="Issuer"
          value="Current User (Auto-assigned)"
          fullWidth
          margin="normal"
          disabled
          helperText="The issuer will be automatically set to the current user"
        />

        {/* High-Risk Options */}
        <Typography variant="h6" color="primary" mt={2} mb={1}>
          High-Risk Activities
        </Typography>
        <FormGroup row>
          {highRiskOptions.map(opt => (
            <FormControlLabel
              key={opt.value}
              control={
                <Checkbox
                  checked={highRisk.includes(opt.value)}
                  onChange={e => handleHighRiskChange(opt.value, e.target.checked)}
                  disabled={!!(props.highRisk || state?.highRisk)}
                  color="secondary"
                />
              }
              label={opt.label}
            />
          ))}
        </FormGroup>

        {/* Required Fields */}
        <TextField
          label="Work Description"
          value={form.description}
          onChange={handleDescriptionChange}
          fullWidth
          margin="normal"
          multiline
          minRows={2}
          required
        />
        <TextField
          label="Work Location"
          value={form.location}
          onChange={handleLocationChange}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          label="Start Time"
          type="datetime-local"
          value={form.start}
          onChange={handleStartTimeChange}
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
          required
        />
        <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={setStartTimeToNow}
            startIcon={<AccessTime />}
          >
            Now
          </Button>
        </Stack>
        <TextField
          label="End Time"
          type="datetime-local"
          value={form.end}
          onChange={handleEndTimeChange}
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
          required
        />
        <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={addDayToEndTime}
            startIcon={<CalendarToday />}
          >
            +1 Day
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={addWeekToEndTime}
            startIcon={<CalendarToday />}
          >
            +1 Week
          </Button>
        </Stack>

        {/* Type-specific fields */}
        {renderTypeSpecificFields()}

        {/* API feedback */}
        {mutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(mutation.error as Error)?.message || 'Error creating permit'}
          </Alert>
        )}
        {mutation.isSuccess && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Permit created successfully!
          </Alert>
        )}
        {mutation.isPending && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress />
          </Box>
        )}

        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button 
            variant="contained" 
            color="primary" 
            type="submit" 
            disabled={mutation.isPending}
            size="large"
          >
            {mutation.isPending ? 'Creating...' : 'Create Permit'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default PermitForm; 